import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Container,
  Form,
  Button,
  Card,
  Alert,
  Spinner,
  Row,
  Col,
  InputGroup,
} from "react-bootstrap";
import {
  FaUser,
  FaLock,
  FaStore,
  FaBuilding,
  FaPhone,
  FaEnvelope,
  FaUserTag,
  FaKey,
  FaCheckCircle,
} from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL;
const SECRET_CODE = "vm2002@DB!";

function Signup() {
  const navigate = useNavigate();

  // Form states
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    branch_id: "",
    branch_name: "",
    full_name: "",
    email: "",
    phone: "",
    secretCode: "",
    monthly_charge: 0,
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordValid, setPasswordValid] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validate password on change
    if (name === "password") {
      validatePassword(value);
    }
  };

  const validatePassword = (password) => {
    setPasswordValid({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*]/.test(password),
    });
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }

    if (formData.secretCode !== SECRET_CODE) {
      setError("Invalid secret code");
      return false;
    }

    // Check all password requirements
    if (!Object.values(passwordValid).every(Boolean)) {
      setError("Password does not meet all requirements");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // console.log(formData);

      const response = await fetch(`${API_URL}/auth/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          branch_id: formData.branch_id,
          branch_name: formData.branch_name,
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
          role: "admin",
          permissions: ["all"], // Default permissions for admin
          monthly_charge: formData.monthly_charge,
          is_active: true,
        }),
      });
      //console.log(body);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Signup failed");
      }

      setSuccess("Admin user created successfully! Redirecting to login...");

      // Clear form
      setFormData({
        username: "",
        password: "",
        confirmPassword: "",
        branch_id: "",
        branch_name: "",
        full_name: "",
        email: "",
        phone: "",
        monthly_charge: 0,
        secretCode: "",
      });

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      fluid
      className="d-flex justify-content-center align-items-center p-3"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
      }}
    >
      {/* Background Decorations */}
      <div
        style={{
          position: "absolute",
          top: "-10%",
          right: "-5%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.1)",
          zIndex: 1,
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-10%",
          left: "-5%",
          width: "350px",
          height: "350px",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.1)",
          zIndex: 1,
        }}
      />

      {/* Signup Card */}
      <Card
        className="border-0 shadow-lg"
        style={{
          width: "100%",
          maxWidth: "800px",
          borderRadius: "20px",
          zIndex: 2,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <Card.Body className="p-5">
          {/* Header */}
          <div className="text-center mb-4">
            <h2 style={{ color: "#333", fontWeight: "700" }}>
              Admin Registration
            </h2>
            <p className="text-muted">Create a new administrator account</p>
          </div>

          {/* Alerts */}
          {error && (
            <Alert
              variant="danger"
              className="border-0"
              style={{ borderRadius: "10px" }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              variant="success"
              className="border-0"
              style={{ borderRadius: "10px" }}
            >
              <FaCheckCircle className="me-2" />
              {success}
            </Alert>
          )}

          {/* Secret Code Warning */}
          <Alert
            variant="warning"
            className="border-0 mb-4"
            style={{ borderRadius: "10px" }}
          >
            <strong>⚠️ Admin Access Only:</strong> You need the secret code to
            register as an admin.
          </Alert>

          <Form onSubmit={handleSubmit}>
            <Row>
              {/* Username */}
              <Col md={6} className="mb-3">
                <Form.Label>Username *</Form.Label>
                <InputGroup>
                  <InputGroup.Text style={{ background: "#f8f9fa" }}>
                    <FaUser style={{ color: "#667eea" }} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    placeholder="Enter username"
                    required
                    disabled={loading}
                  />
                </InputGroup>
              </Col>

              {/* Full Name */}
              <Col md={6} className="mb-3">
                <Form.Label>Full Name</Form.Label>
                <InputGroup>
                  <InputGroup.Text style={{ background: "#f8f9fa" }}>
                    <FaUserTag style={{ color: "#667eea" }} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder="Enter full name"
                    disabled={loading}
                  />
                </InputGroup>
              </Col>

              {/* Email */}
              <Col md={6} className="mb-3">
                <Form.Label>Email</Form.Label>
                <InputGroup>
                  <InputGroup.Text style={{ background: "#f8f9fa" }}>
                    <FaEnvelope style={{ color: "#667eea" }} />
                  </InputGroup.Text>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email"
                    disabled={loading}
                  />
                </InputGroup>
              </Col>

              {/* Phone */}
              <Col md={6} className="mb-3">
                <Form.Label>Phone</Form.Label>
                <InputGroup>
                  <InputGroup.Text style={{ background: "#f8f9fa" }}>
                    <FaPhone style={{ color: "#667eea" }} />
                  </InputGroup.Text>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                    disabled={loading}
                  />
                </InputGroup>
              </Col>

              {/* Branch ID */}
              <Col md={6} className="mb-3">
                <Form.Label>Branch ID *</Form.Label>
                <InputGroup>
                  <InputGroup.Text style={{ background: "#f8f9fa" }}>
                    <FaStore style={{ color: "#667eea" }} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="branch_id"
                    value={formData.branch_id}
                    onChange={handleChange}
                    placeholder="Enter branch ID"
                    required
                    disabled={loading}
                  />
                </InputGroup>
              </Col>

              {/* Branch Name */}
              <Col md={6} className="mb-3">
                <Form.Label>Branch Name</Form.Label>
                <InputGroup>
                  <InputGroup.Text style={{ background: "#f8f9fa" }}>
                    <FaBuilding style={{ color: "#667eea" }} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="branch_name"
                    value={formData.branch_name}
                    onChange={handleChange}
                    placeholder="Enter branch name"
                    disabled={loading}
                  />
                </InputGroup>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label>Monthly Charge</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    name="monthly_charge"
                    value={formData.monthly_charge}
                    onChange={handleChange}
                    placeholder="Enter monthly charge amount"
                    disabled={loading}
                    step="0.01"
                    min="0"
                    className="text-end"
                    style={{ fontFamily: "monospace" }}
                  />
                  <InputGroup.Text style={{ background: "#f8f9fa" }}>
                    KES
                  </InputGroup.Text>
                </InputGroup>
                <Form.Text className="text-muted">
                  Enter the monthly subscription charge (e.g., 1500.00)
                </Form.Text>
              </Col>
              {/* Password */}
              <Col md={6} className="mb-3">
                <Form.Label>Password *</Form.Label>
                <InputGroup>
                  <InputGroup.Text style={{ background: "#f8f9fa" }}>
                    <FaLock style={{ color: "#667eea" }} />
                  </InputGroup.Text>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter password"
                    required
                    disabled={loading}
                  />
                </InputGroup>
              </Col>

              {/* Confirm Password */}
              <Col md={6} className="mb-3">
                <Form.Label>Confirm Password *</Form.Label>
                <InputGroup>
                  <InputGroup.Text style={{ background: "#f8f9fa" }}>
                    <FaLock style={{ color: "#667eea" }} />
                  </InputGroup.Text>
                  <Form.Control
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm password"
                    required
                    disabled={loading}
                    isInvalid={
                      formData.password !== formData.confirmPassword &&
                      formData.confirmPassword !== ""
                    }
                  />
                </InputGroup>
              </Col>

              {/* Secret Code */}
              <Col md={12} className="mb-4">
                <Form.Label>Secret Code *</Form.Label>
                <InputGroup>
                  <InputGroup.Text style={{ background: "#f8f9fa" }}>
                    <FaKey style={{ color: "#667eea" }} />
                  </InputGroup.Text>
                  <Form.Control
                    type="password"
                    name="secretCode"
                    value={formData.secretCode}
                    onChange={handleChange}
                    placeholder="Enter admin secret code"
                    required
                    disabled={loading}
                  />
                </InputGroup>
                <Form.Text className="text-muted">
                  Required for admin registration. Contact system administrator
                  if you don't have it.
                </Form.Text>
              </Col>
            </Row>

            {/* Password Requirements */}
            {formData.password && (
              <div className="mb-4 p-3 bg-light rounded">
                <small className="text-muted d-block mb-2">
                  Password Requirements:
                </small>
                <Row>
                  <Col xs={6} md={4}>
                    <small
                      className={
                        passwordValid.length ? "text-success" : "text-danger"
                      }
                    >
                      ✓ At least 8 characters
                    </small>
                  </Col>
                  <Col xs={6} md={4}>
                    <small
                      className={
                        passwordValid.uppercase ? "text-success" : "text-danger"
                      }
                    >
                      ✓ One uppercase letter
                    </small>
                  </Col>
                  <Col xs={6} md={4}>
                    <small
                      className={
                        passwordValid.lowercase ? "text-success" : "text-danger"
                      }
                    >
                      ✓ One lowercase letter
                    </small>
                  </Col>
                  <Col xs={6} md={4}>
                    <small
                      className={
                        passwordValid.number ? "text-success" : "text-danger"
                      }
                    >
                      ✓ One number
                    </small>
                  </Col>
                  <Col xs={6} md={4}>
                    <small
                      className={
                        passwordValid.special ? "text-success" : "text-danger"
                      }
                    >
                      ✓ One special character (!@#$%^&*)
                    </small>
                  </Col>
                </Row>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              className="w-100 border-0 mb-3"
              disabled={loading}
              style={{
                height: "50px",
                borderRadius: "10px",
                fontSize: "1rem",
                fontWeight: "600",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              }}
            >
              {loading ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Creating Account...
                </>
              ) : (
                "Create Admin Account"
              )}
            </Button>

            {/* Login Link */}
            <div className="text-center">
              <small className="text-muted">
                Already have an account?{" "}
                <Link
                  to="/login"
                  style={{ color: "#667eea", textDecoration: "none" }}
                >
                  Sign in
                </Link>
              </small>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default Signup;
